import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Button, Badge, Input } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';

interface ComplaintTrack {
  ticketNumber: string;
  type: string;
  subject: string;
  status: string;
  priority: string;
  response: string | null;
  respondedAt: string | null;
  slaDeadline: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reporterName: string;
}

const STATUS_LABEL: Record<string, { label: string; color: 'amber' | 'navy' | 'green' | 'red' | 'gold' }> = {
  NEW:           { label: 'جديدة',             color: 'amber' },
  IN_REVIEW:     { label: 'قيد المراجعة',       color: 'navy' },
  AWAITING_REPLY: { label: 'بانتظار ردّكم',    color: 'gold' },
  RESOLVED:      { label: 'مُغلَقة بحل',        color: 'green' },
  REJECTED:      { label: 'مرفوضة',             color: 'red' },
  ESCALATED:     { label: 'مُحالة لجهة عليا',   color: 'navy' },
};

const TYPE_LABEL: Record<string, string> = {
  PROPERTY_AD: 'إعلان عقاري', AUCTION: 'مزاد', BROKER: 'مسوّق',
  PAYMENT: 'دفع/اشتراك', HARASSMENT: 'تحرّش', PRIVACY: 'خصوصية',
  COPYRIGHT: 'حقوق ملكية', OTHER: 'غير ذلك',
};

/**
 * 🔍 صفحة متابعة شكوى — مفتوحة بدون تسجيل دخول، فقط برقم التذكرة.
 */
export function ComplaintTrackPage() {
  const { ticket = '' } = useParams<{ ticket: string }>();
  const navigate = useNavigate();
  const [input, setInput] = useState(ticket);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['complaint', 'track', ticket],
    queryFn: () => api.get<ApiSuccess<ComplaintTrack>>(`/api/complaints/track/${ticket}`),
    enabled: !!ticket,
    retry: false,
  });

  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white min-h-[70vh]">
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-5xl mb-3">🔍</div>
          <h1 className="text-2xl md:text-3xl font-black mb-2">متابعة شكوى</h1>
          <p className="text-gold-light text-sm">اكتب رقم التذكرة الذي وصلك عبر الإيميل.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        <Card><CardBody>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                label="رقم التذكرة"
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                placeholder="SOR-2026-00001"
                dir="ltr"
              />
            </div>
            <Button variant="primary" onClick={() => navigate(`/complaints/track/${input.trim()}`)}>متابعة</Button>
          </div>
        </CardBody></Card>

        {ticket && isLoading && <div className="text-center py-8 text-gray-500">⟳ جاري البحث…</div>}

        {ticket && error && (
          <Card><CardBody>
            <div className="text-center py-8">
              <div className="text-5xl mb-3">❓</div>
              <h2 className="font-black text-navy-dark mb-2">رقم التذكرة غير موجود</h2>
              <p className="text-sm text-gray-600">تحقق من الرقم وحاول مرة أخرى. أو <Link to="/complaints" className="text-navy hover:underline font-bold">قدّم شكوى جديدة</Link>.</p>
            </div>
          </CardBody></Card>
        )}

        {data?.data && (
          <Card>
            <CardBody className="space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap pb-3 border-b">
                <div>
                  <div className="text-xs text-gray-500 mb-1">رقم التذكرة</div>
                  <div className="text-xl font-black text-navy-dark" style={{ fontFamily: 'monospace' }}>{data.data.ticketNumber}</div>
                </div>
                <Badge color={STATUS_LABEL[data.data.status]?.color ?? 'navy'}>
                  {STATUS_LABEL[data.data.status]?.label ?? data.data.status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Detail label="النوع" value={TYPE_LABEL[data.data.type] ?? data.data.type} />
                <Detail label="الموضوع" value={data.data.subject} />
                <Detail label="المُشتكي" value={data.data.reporterName} />
                <Detail label="تاريخ التقديم" value={new Date(data.data.createdAt).toLocaleString('ar-SA')} />
                {data.data.slaDeadline && !data.data.resolvedAt && (
                  <Detail label="موعد الرد الأقصى" value={new Date(data.data.slaDeadline).toLocaleDateString('ar-SA')} />
                )}
                {data.data.resolvedAt && (
                  <Detail label="تاريخ الإغلاق" value={new Date(data.data.resolvedAt).toLocaleString('ar-SA')} />
                )}
              </div>

              {data.data.response && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">الردّ الرسمي</div>
                  <div className="bg-navy-soft/30 border-r-4 border-gold rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
                    {data.data.response}
                  </div>
                  {data.data.respondedAt && (
                    <div className="text-xs text-gray-500 mt-2">📨 أُرسل في {new Date(data.data.respondedAt).toLocaleString('ar-SA')}</div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t">
                <Button variant="ghost" onClick={() => refetch()}>↻ تحديث</Button>
                <Link to="/complaints"><Button variant="primary">شكوى جديدة</Button></Link>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="font-bold text-navy-dark">{value}</div>
    </div>
  );
}
